<script lang="ts">
  import { setContext } from 'svelte';
  import type { LayoutProps } from './$types';
  import type { SkillLogContext } from '$lib/componentTypes';
  import { SvelteMap } from 'svelte/reactivity';
  import type { SkillLog } from '$lib/api/types';
  import { goto, invalidate } from '$app/navigation';
  import { addSkillLogEntries } from '$lib/api/studentActions';
  import { isClassOpen } from '$lib/util';
  let props: LayoutProps = $props();

  // user will select the skills in the first screen, and those skills will be stored here when navigating to the next
  // page
  let orderedSelectedSkills: number[] = $state([]);

  // self-assessment level set by the user as they adjust the slider for each skill they marked. stored here to retain
  // across page navigations until the final one, which is collected into a SkillLog object and posted to the API.
  let proficiencyLevels = new SvelteMap<number, number>();

  // loading, error, and fetch response states saved in context to persist as we navigate from the last page of the self
  // assessment to the confirmation page.
  let isPosting = $state(false);
  let postError: string | null = $state(null);
  let loggedSkillCount = $state(0);
  let updatedCategoryIds: number[] = $state([]);
  let updatedUserRank: number | null = $state(null);
  let formerIncompleteBadgesOfUpdatedCategories: number[] = $state([]);

  // redirect users if they try to access this page after the class ended
  props.data.classPromise.then(x => {
    if (!isClassOpen(x.class)) {
      goto(`/classes/${props.params.id}`, { replaceState: true });
    }
  });

  setContext<SkillLogContext>('skillLog', {
    getSelectedSkills: () => {
      return orderedSelectedSkills;
    },
    setOrderedSelectedSkills: (skillIds: number[]) => {
      orderedSelectedSkills = skillIds;
    },
    setProficiencyLevel: (skillId: number, level: number) => {
      proficiencyLevels.set(skillId, level);
    },
    getProficiencyLevel: (skillId: number) => proficiencyLevels.get(skillId),
    postSkillLog: async () => {
      let loggedSkills: SkillLog[] = [];
      for (const [skillId, selfAssessmentLevel] of proficiencyLevels.entries()) {
        loggedSkills.push({ skillId, selfAssessmentLevel });
      }

      loggedSkillCount = loggedSkills.length;
      formerIncompleteBadgesOfUpdatedCategories = [];
      updatedCategoryIds = [];
      const theclass = await props.data.classPromise;
      const classSkills = theclass.skills;

      // collect each category that had a skill logged
      for (const skill of classSkills) {
        if (proficiencyLevels.has(skill.skillId) && !updatedCategoryIds.includes(skill.skillCategoryId)) {
          updatedCategoryIds.push(skill.skillCategoryId);

          // note the incompleted badges. the ones that are complete after this will get an animation
          const categoryBadgeProgress = theclass.badgeProgress.find(x => x.skillCategoryId === skill.skillCategoryId);
          if (
            categoryBadgeProgress &&
            categoryBadgeProgress.completedSkillCount < categoryBadgeProgress.possibleSkillCount
          ) {
            formerIncompleteBadgesOfUpdatedCategories.push(categoryBadgeProgress.skillCategoryId);
          } else if (!categoryBadgeProgress) {
            // if the category is not in badgeProgress, no progress is made at all
            formerIncompleteBadgesOfUpdatedCategories.push(skill.skillCategoryId);
          }
        }
      }

      isPosting = true;
      const classId = parseInt(props.params.id);
      await goto(`/classes/${classId}/log/submitted`, { replaceState: true });
      orderedSelectedSkills = [];
      proficiencyLevels.clear();

      try {
        const updatedRank = await addSkillLogEntries(classId, loggedSkills);
        isPosting = false;
        updatedUserRank = updatedRank.studentRank ?? null;

        invalidate(url => url.pathname.startsWith(`/api/studentactions/classes/${classId}`));
      } catch (e) {
        isPosting = false;
        if (e instanceof Error) {
          postError = e.message;
        } else {
          postError = `${e}`;
        }
        throw e;
      }
    },
    getIsPosting: () => isPosting,
    getPostError: () => postError,
    getUpdatedLeaderboard: () => {
      return {
        loggedSkillCount,
        updatedCategoryIds,
        userRank: updatedUserRank,
        formerIncompleteBadgesOfUpdatedCategories,
      };
    },
  });
</script>

<main id="main-container" class="container py-3">
  {@render props.children?.()}
</main>
